import { z } from 'zod';
import {
  contentPartSchema,
  conversationClassificationSchema,
  conversationKindSchema,
  conversationLifecycleSchema,
  messageRoleSchema,
  participantTypeSchema,
} from './chat';
import { participantRoleClassSchema } from './role-class';

/**
 * camelCase wire projections for the participant-first conversation spine.
 * Member mobile and internal Atlas views are separate audience contracts;
 * neither exposes canonical member/staff/provider identity keys. Those keys
 * remain server-side authorization inputs, never presentation data.
 *
 * The general conversation projection exposes the conversation axes and
 * membership ONLY. Source metadata — provider conversation ids,
 * agent ids, ingestion status, flattened transcript text, transcript
 * projections — is deliberately absent and MUST NOT be added here; source
 * data stays on source-specific read paths.
 */

// ─── Conversation (general projection) ───────────────────────────────────────

export const CHAT_CONVERSATION_COLUMN_FIELDS = [
  'id',
  'kind',
  'classification',
  'lifecycle',
  'createdAt',
] as const;

export const chatConversationSchema = z
  .object({
    id: z.string().uuid(),
    kind: conversationKindSchema,
    classification: conversationClassificationSchema,
    lifecycle: conversationLifecycleSchema,
    createdAt: z.string(),
  })
  .strict();
export type ChatConversation = z.infer<typeof chatConversationSchema>;

// ─── Participant ──────────────────────────────────────────────────────────────

export const CONVERSATION_PARTICIPANT_COLUMN_FIELDS = [
  'id',
  'conversationId',
  'participantType',
  'joinedAt',
  'leftAt',
] as const;

export const conversationParticipantSchema = z
  .object({
    id: z.string().uuid(),
    conversationId: z.string().uuid(),
    participantType: participantTypeSchema,
    joinedAt: z.string(),
    leftAt: z.string().nullable(),
  })
  .strict();
export type ConversationParticipant = z.infer<typeof conversationParticipantSchema>;

export const memberConversationParticipantSchema = conversationParticipantSchema
  .extend({
    isSelf: z.boolean(),
    displayName: z.string().min(1).nullable(),
  })
  .strict();
export type MemberConversationParticipant = z.infer<typeof memberConversationParticipantSchema>;

export const atlasConversationParticipantSchema = conversationParticipantSchema
  .extend({
    isSelf: z.boolean(),
    displayName: z.string().min(1).nullable(),
    // Present only on internal-user participants the server could classify.
    // The member audience deliberately never receives this key: shipped member
    // clients parse participants strictly, so the member projection stays
    // key-stable and the class is an internal-surface field until the member
    // contract migrates.
    roleClass: participantRoleClassSchema.optional(),
  })
  .strict();
export type AtlasConversationParticipant = z.infer<typeof atlasConversationParticipantSchema>;

// ─── Message ──────────────────────────────────────────────────────────────────
//
// `parts` is the wire name for the stored `content` ContentPart[] — the one
// hand-authored rename in this contract. Positions are monotonic but not
// gapless; clients cursor with `position > N` and never assume contiguity.

export const CONVERSATION_MESSAGE_COLUMN_FIELDS = [
  'id',
  'conversationId',
  'role',
  'senderParticipantId',
  'position',
  'clientMessageId',
  'correctsMessageId',
  'createdAt',
] as const;

// Message positions may be negative: backfilled history is written below a
// thread's first message, so the coordinate is any safe integer.
export const conversationPositionSchema = z.number().int().safe();

// Read watermarks mirror the store's non-negative CHECK constraints, so they do
// not inherit the widened message-position range.
export const conversationReadWatermarkPositionSchema = z.number().int().nonnegative().safe();

export const conversationMessageSchema = z
  .object({
    id: z.string().uuid(),
    conversationId: z.string().uuid(),
    role: messageRoleSchema,
    senderParticipantId: z.string().uuid().nullable(),
    parts: z.array(contentPartSchema),
    position: conversationPositionSchema,
    clientMessageId: z.string().nullable(),
    correctsMessageId: z.string().uuid().nullable(),
    createdAt: z.string(),
  })
  .strict();
export type ChatMessage = z.infer<typeof conversationMessageSchema>;

// ─── Read state ───────────────────────────────────────────────────────────────

export const CONVERSATION_READ_STATE_COLUMN_FIELDS = [
  'conversationId',
  'participantId',
  'lastDeliveredPosition',
  'lastReadPosition',
  'updatedAt',
] as const;

export const conversationReadWatermarkShape = {
  lastDeliveredPosition: conversationReadWatermarkPositionSchema.nullable(),
  lastReadPosition: conversationReadWatermarkPositionSchema.nullable(),
} as const;

export function hasValidConversationReadWatermarks(state: {
  lastDeliveredPosition: number | null;
  lastReadPosition: number | null;
}): boolean {
  return (
    state.lastReadPosition === null ||
    (state.lastDeliveredPosition !== null && state.lastReadPosition <= state.lastDeliveredPosition)
  );
}

export const conversationReadStateSchema = z
  .object({
    conversationId: z.string().uuid(),
    participantId: z.string().uuid(),
    ...conversationReadWatermarkShape,
    updatedAt: z.string(),
  })
  .strict()
  // Read requires delivery, and read never exceeds delivered — the same
  // invariant the store's CHECK enforces.
  .refine(hasValidConversationReadWatermarks, {
    message: 'read watermark requires a delivered watermark at or beyond it',
    path: ['lastReadPosition'],
  });
export type ConversationReadState = z.infer<typeof conversationReadStateSchema>;
